import { AnimatePresence, motion } from 'framer-motion'
import { MinusIcon } from '../icons/minus'
import { PlusIcon } from '../icons/plus'
import { useAppSelector } from '@/redux/hooks'

const numberVariants = {
    initial: { y: 20, opacity: 0 },
    animate: {
        y: 0,
        opacity: 1,
        transition: { type: 'spring', stiffness: 500, damping: 25 }
    },
    exit: {
        y: -20,
        opacity: 0,
        transition: { duration: 0.1 }
    }
}

interface QuantitySelectorProps {
    minValue: number
    maxValue?: number
    value: number
    onValueChange: (value: number) => void
}

export const QuantitySelector = ({
    minValue,
    maxValue,
    value,
    onValueChange
}: QuantitySelectorProps) => {
    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const isHoff = settings.ecommerce_group === 'hoff'
    const isMisako = settings.ecommerce_group === 'misako'

    if (isHoff) {
        return (
            <div className="flex shrink-0 items-center justify-center gap-3.5 rounded-md border border-solid border-neutral-900 px-2 py-1">
                <button
                    aria-label="Decrease quantity"
                    type="button"
                    disabled={value === minValue}
                    onClick={() => onValueChange(value - 1)}
                    className="flex shrink-0 items-center justify-center rounded-full text-neutral-900 disabled:text-neutral-300"
                >
                    <MinusIcon width={18} height={18} />
                </button>
                <div className="flex w-3 items-center justify-center font-haas text-sm tabular-nums">
                    {value}
                </div>
                <button
                    aria-label="Increase quantity"
                    type="button"
                    disabled={value === maxValue}
                    onClick={() => onValueChange(value + 1)}
                    className="flex shrink-0 items-center justify-center disabled:text-neutral-300"
                >
                    <PlusIcon width={18} height={18} strokeWidth={2} />
                </button>
            </div>
        )
    }

    if (isMisako) {
        return (
            <div className="flex shrink-0 items-center justify-center gap-0.5">
                <button
                    aria-label="Decrease quantity"
                    type="button"
                    disabled={value === minValue}
                    onClick={() => onValueChange(value - 1)}
                    className="flex shrink-0 items-center justify-center border border-solid border-neutral-900 text-neutral-900 disabled:border-neutral-300 disabled:text-neutral-300"
                >
                    <MinusIcon width={22} height={22} strokeWidth={1} />
                </button>
                <div className="relative flex h-10 w-11 shrink-0 items-center justify-center overflow-hidden">
                    <AnimatePresence mode="wait" initial={false}>
                        <motion.span
                            key={value}
                            variants={numberVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            className="absolute font-sans text-2xl tabular-nums text-gray-800"
                        >
                            {value}
                        </motion.span>
                    </AnimatePresence>
                </div>
                <button
                    aria-label="Increase quantity"
                    type="button"
                    disabled={value === maxValue}
                    onClick={() => onValueChange(value + 1)}
                    className="flex shrink-0 items-center justify-center border border-solid border-neutral-900 text-neutral-900 disabled:border-neutral-300 disabled:text-neutral-300"
                >
                    <PlusIcon width={22} height={22} strokeWidth={1} />
                </button>
            </div>
        )
    }

    return (
        <div className="flex shrink-0 items-center justify-center gap-0.5">
            <button
                aria-label="Decrease quantity"
                type="button"
                disabled={value === minValue}
                onClick={() => onValueChange(value - 1)}
                className="flex shrink-0 items-center justify-center rounded-full border border-solid border-neutral-900 text-neutral-900 disabled:border-neutral-300 disabled:text-neutral-300"
            >
                <MinusIcon width={22} height={22} strokeWidth={1} />
            </button>
            <div className="relative flex h-10 w-11 shrink-0 items-center justify-center overflow-hidden">
                <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                        key={value}
                        variants={numberVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        className="absolute font-sans text-2xl tabular-nums text-gray-800"
                    >
                        {value}
                    </motion.span>
                </AnimatePresence>
            </div>
            <button
                aria-label="Increase quantity"
                type="button"
                disabled={value === maxValue}
                onClick={() => onValueChange(value + 1)}
                className="flex shrink-0 items-center justify-center rounded-full border border-solid border-neutral-900 text-neutral-900 disabled:border-neutral-300 disabled:text-neutral-300"
            >
                <PlusIcon width={22} height={22} strokeWidth={1} />
            </button>
        </div>
    )
}
